import { ASPECTS, SIGNS, SIGN_GLYPHS, ORB_FACTORS, DEFINITIONAL_PAIRS } from '../data/constants';

export interface AspectMatch {
  name: string;
  angle: number;
  orb: number;
  color: string;
  symbol: string;
  major: boolean;
  orb_actual: number;
}

export interface ChartAspect extends AspectMatch {
  p1: string;
  p2: string;
}

export function aspectBetween(lon1: number, lon2: number, includeMinor: boolean): AspectMatch | null {
  let diff = Math.abs(lon1 - lon2);
  if (diff > 180) diff = 360 - diff;
  for (const asp of ASPECTS) {
    if (!includeMinor && !asp.major) continue;
    const delta = Math.abs(diff - asp.angle);
    if (delta <= asp.orb) return { ...asp, orb_actual: delta };
  }
  return null;
}

/** True when the angle between two points is fixed by definition. */
export function isDefinitionalPair(a: string, b: string): boolean {
  if (a === b) return true;
  return DEFINITIONAL_PAIRS.some(([x, y]) => (a === x && b === y) || (a === y && b === x));
}

/**
 * Aspect test that scales each aspect's orb by the two bodies involved, and
 * refuses pairs whose separation is definitional (Node/South Node, Asc/MC).
 */
export function aspectBetweenPoints(
  nameA: string,
  lonA: number,
  nameB: string,
  lonB: number,
  includeMinor: boolean,
  /** Synastry compares two different charts, so Sun-Sun etc. is meaningful. */
  allowSamePoint = false,
): AspectMatch | null {
  if (!allowSamePoint && isDefinitionalPair(nameA, nameB)) return null;
  const factor = Math.min(ORB_FACTORS[nameA] ?? 1, ORB_FACTORS[nameB] ?? 1);
  let diff = Math.abs(lonA - lonB);
  if (diff > 180) diff = 360 - diff;
  for (const asp of ASPECTS) {
    if (!includeMinor && !asp.major) continue;
    const delta = Math.abs(diff - asp.angle);
    if (delta <= asp.orb * factor) return { ...asp, orb: asp.orb * factor, orb_actual: delta };
  }
  return null;
}

export function signOf(lon: number): { sign: string; glyph: string; degree: number; index: number } {
  const safe = Number.isFinite(lon) ? ((lon % 360) + 360) % 360 : 0;
  const s = Math.min(11, Math.max(0, Math.floor(safe / 30)));
  const deg = safe - s * 30;
  return { sign: SIGNS[s], glyph: SIGN_GLYPHS[s], degree: deg, index: s };
}

export function formatPosition(lon: number, retrograde = false): string {
  const { glyph, degree } = signOf(lon);
  const d = Math.floor(degree);
  const m = Math.floor((degree - d) * 60);
  return `${d}° ${glyph} ${String(m).padStart(2,'0')}'${retrograde ? ' ℞' : ''}`;
}